import { AlertTriangle, ChevronDown, ChevronUp, Info, Search, Sparkles } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import type {
  PhraseDescription,
  ProductSearchDescriptors,
  SearchPivot,
} from '~/features/catalog-operations/types/catalog-operations.types';
import { hasDescriptionContent } from '~/features/catalog-operations/utils/phrase-description';
import { cn } from '~/utils/cn';
import { formatDateTime } from '~/utils/date';

interface Props {
  /** Both halves of the search-descriptor read for this product. `null` means the
   *  endpoint 404'd — neither strategy ever ran. `undefined` while in flight. */
  data: ProductSearchDescriptors | null | undefined;
}

/** Human-readable label per run status. Unrecognised values pass through
 *  de-underscored rather than being hidden — the backend enum can grow, and a
 *  status the UI hasn't seen is still information. */
function statusLabel(status: string): string {
  if (status === 'applied') return 'Applied';
  if (status === 'llm_generated') return 'LLM-generated';
  if (status === 'no_tag_breakdown') return 'No tag breakdown';
  if (status === 'error') return 'Error';
  if (status === 'unknown') return 'Unknown';
  return status.replace(/_/g, ' ');
}

/** Badge tone per status. `applied` is the good outcome; `llm_generated` and
 *  `no_tag_breakdown` are degraded-but-real results, so they warn rather than
 *  error — the phrases they produced are still usable. */
function statusClass(status: string): string {
  if (status === 'applied') return 'bg-success/10 text-success';
  if (status === 'error') return 'bg-error/10 text-error';
  if (status === 'llm_generated' || status === 'no_tag_breakdown')
    return 'bg-warning/10 text-warning';
  return 'bg-muted text-muted-foreground';
}

/**
 * Normalises the agent's self-rating to a 0–1 ratio.
 *
 * The contract documents 0–1 (`0.93`), but runs come back on a 0–10 scale too
 * (`6.00` observed in dev), which rendered as "600%". The scale is inferred from
 * magnitude rather than trusted, and anything that fits no scale returns null so
 * the indicator hides instead of printing a wrong number.
 *
 * A bare `1` is read as a perfect 0–1 score, not 1/10 — the two are
 * indistinguishable from the value alone, so the documented scale wins.
 */
function accuracyRatio(score: number): number | null {
  if (!Number.isFinite(score) || score < 0) return null;
  if (score <= 1) return score;
  if (score <= 10) return score / 10;
  if (score <= 100) return score / 100;
  return null;
}

/** Why the search-descriptor half came back empty, in the run's own terms. The
 *  generic "no descriptors" reads as a dead end; the run status usually says
 *  more than that. */
function searchEmptyBody(status: string | null): string {
  if (status === 'no_tag_breakdown')
    return 'The run found no attributes on this product eligible to pivot on.';
  if (status === 'error') return 'The search-descriptor step failed for this product.';
  if (status === null)
    return 'The search-descriptor step did not run for this product’s latest enrichment.';
  return 'The search-descriptor step ran but produced no phrases for this product.';
}

/** Caveat when the phrases didn't come from the tenant's keyword index. The
 *  phrases are still the run's real output — only the volume figures are
 *  unreliable, which is why this reads as a note rather than a failure. */
function keywordSourceNote(source: string | null): string | null {
  if (source === null || source === 'qdrant') return null;
  return `Phrases were generated without the tenant keyword index (source: ${source}), so search volumes may be missing or estimated. The phrases themselves are unaffected.`;
}

/** Monthly search volume, or a dash. `null` is expected for a phrase the model
 *  invented that isn't in the tenant keyword pool — never an error. */
function VolumeBadge({ volume }: { volume: number | null }) {
  if (volume === null) {
    return <span className="text-muted-foreground flex-none text-xs tabular-nums">—</span>;
  }
  return (
    <span className="text-muted-foreground flex-none text-xs tabular-nums">
      {volume.toLocaleString()}
      <span className="ml-0.5 text-[10px]">/mo</span>
    </span>
  );
}

/** Status pill shared by the run header and the phrase-description header. */
function StatusBadge({ status, testId }: { status: string; testId?: string }) {
  return (
    <Badge
      data-testid={testId}
      variant="outline"
      className={cn('rounded-md border-0', statusClass(status))}
    >
      {statusLabel(status)}
    </Badge>
  );
}

/**
 * The tab's one accordion row, shared by pivot groups and description levels so
 * both halves collapse the same way. Follows `enriched-attribute-row.tsx` — the
 * house pattern for this, since there is no `accordion` primitive in `ui/`.
 *
 * `action` renders *beside* the trigger rather than inside it: a button nested in
 * a button is invalid markup, and a copy click must not toggle the row.
 */
function CollapsibleCard({
  icon,
  title,
  subtitle,
  meta,
  action,
  defaultOpen = true,
  testId,
  children,
}: {
  icon?: ReactNode;
  title: string;
  /** One-phrase gloss beside the title, so a collapsed row says what it holds. */
  subtitle?: string;
  /** Summary shown while collapsed — the row has to be worth reading shut. */
  meta?: ReactNode;
  action?: ReactNode;
  /** Open on mount. Defaults to open: everything on this tab is the answer the
   *  user came for, so it is all readable without a click, and collapsing is
   *  there to get long content out of the way once read. */
  defaultOpen?: boolean;
  /** Automation hook: root gets `testId`, the trigger `${testId}-toggle-button`. */
  testId?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible
      data-testid={testId}
      open={open}
      onOpenChange={setOpen}
      className="rounded-xl border"
    >
      <div className="flex items-center">
        <CollapsibleTrigger asChild>
          <Button
            data-testid={testId ? `${testId}-toggle-button` : undefined}
            variant="ghost"
            aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
            className="flex h-auto min-w-0 flex-1 cursor-pointer items-center justify-between gap-3 rounded-none px-4 py-3 hover:bg-transparent"
          >
            <span className="flex min-w-0 items-center gap-2">
              {icon}
              <span className="text-foreground truncate text-sm leading-5 font-semibold">
                {title}
              </span>
              {subtitle ? (
                <span className="text-muted-foreground truncate text-xs font-normal">
                  {subtitle}
                </span>
              ) : null}
            </span>
            <span className="flex flex-none items-center gap-2.5">
              {meta}
              {open ? (
                <ChevronUp className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
              ) : (
                <ChevronDown className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
              )}
            </span>
          </Button>
        </CollapsibleTrigger>
        {action ? <span className="flex-none pr-3">{action}</span> : null}
      </div>
      <CollapsibleContent className="border-t">{children}</CollapsibleContent>
    </Collapsible>
  );
}

/** One pivot and the phrases beneath it. Rendered in array order — the server
 *  returns both levels pre-sorted by rank, so re-sorting would lose the ranking.
 *  The phrase count sits in the header so a collapsed pivot still says how much
 *  it is hiding. */
function PivotCard({ pivot, testId }: { pivot: SearchPivot; testId: string }) {
  const count = pivot.associatedPhrases.length;

  return (
    <CollapsibleCard
      testId={testId}
      title={pivot.pivotPhrase}
      icon={<Search className="text-muted-foreground size-3.5 shrink-0" aria-hidden="true" />}
      meta={
        <>
          <span data-testid={`${testId}-phrase-count`} className="text-muted-foreground text-xs">
            {count} {count === 1 ? 'phrase' : 'phrases'}
          </span>
          <VolumeBadge volume={pivot.searchVolume} />
        </>
      }
    >
      {count > 0 ? (
        <div className="flex flex-wrap gap-2 p-4">
          {pivot.associatedPhrases.map((phrase, i) => (
            // The same phrase can legitimately appear under one pivot twice, so
            // the index tie-breaks the key.
            <Badge
              key={`${phrase.phrase}-${String(i)}`}
              data-testid={`${testId}-phrase-${String(i)}`}
              variant="outline"
              className="gap-2 rounded-md px-2 py-1 text-xs font-medium"
            >
              <span className="text-foreground">{phrase.phrase}</span>
              <VolumeBadge volume={phrase.searchVolume} />
            </Badge>
          ))}
        </div>
      ) : (
        <p
          data-testid={`${testId}-empty`}
          className="text-muted-foreground px-4 py-3 text-sm leading-5"
        >
          No associated phrases for this pivot.
        </p>
      )}
    </CollapsibleCard>
  );
}

/** What Level 3 is — the only phrase-description level shown. Levels 1/2 are
 *  intermediate derivations and the original is the retailer's input; none of
 *  them is what Phrase Descriptions delivers, so they're not rendered. */
const LEVEL3_INFO = {
  subtitle: 'rewritten with phrases',
  derivation:
    'A rewrite of the original copy with the search phrases supplied to the model — 2–4 sentences.',
} as const;

/** One description level. `derivation` explains how this level was produced;
 *  `error` is shown in place of the copy when that level failed. */
function DescriptionLevel({
  label,
  subtitle,
  derivation,
  description,
  error,
}: {
  label: string;
  subtitle: string;
  derivation: string;
  description: string | null;
  error?: string | null;
}) {
  // Nothing generated and nothing to report — the level simply wasn't produced,
  // which needs no row of its own.
  if (!description && !error) return null;

  return (
    <CollapsibleCard
      testId={`product-detail-search-descriptors-${label.toLowerCase().replace(/\s+/g, '-')}`}
      title={label}
      subtitle={subtitle}
      meta={
        <>
          {error ? (
            <AlertTriangle className="text-warning size-4 shrink-0" aria-hidden="true" />
          ) : description ? (
            <span className="text-muted-foreground text-xs tabular-nums">
              {description.length.toLocaleString()} chars
            </span>
          ) : null}
        </>
      }
      action={
        <CopyIconButton
          text={description}
          label={`Copy ${label.toLowerCase()} description`}
          copyMessage={`Copied ${label.toLowerCase()} description`}
          testId={`product-detail-search-descriptors-${label.toLowerCase().replace(/\s+/g, '-')}-copy-button`}
        />
      }
    >
      {/* How this level was produced — sits above the copy so it is read first. */}
      <p className="text-muted-foreground bg-surface-blue border-b px-4 py-2 text-xs leading-5">
        {derivation}
      </p>
      {description ? (
        <p
          data-testid={`product-detail-search-descriptors-${label.toLowerCase().replace(/\s+/g, '-')}-description`}
          className="text-foreground px-4 py-3 text-sm leading-5"
        >
          {description}
        </p>
      ) : null}
      {error ? (
        <div
          data-testid={`product-detail-search-descriptors-${label.toLowerCase().replace(/\s+/g, '-')}-error`}
          className="text-muted-foreground flex items-start gap-2 px-4 py-3 text-sm leading-5"
        >
          <AlertTriangle className="text-warning mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>This level could not be generated: {error}</span>
        </div>
      ) : null}
    </CollapsibleCard>
  );
}

/** The phrase-description half — Level 3 (the rewrite with search phrases)
 *  only, since that's the level Phrase Descriptions delivers. */
function PhraseDescriptionSection({ phrase }: { phrase: PhraseDescription }) {
  return (
    <div
      data-testid="product-detail-search-descriptors-phrase-section"
      className="flex flex-col gap-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
          <h3 className="text-foreground text-sm leading-5 font-semibold">Phrase Descriptions</h3>
          {/* Null on a row that recorded a run without producing anything —
              rendering it would put the literal string "null" in a badge. */}
          {phrase.status ? (
            <StatusBadge
              testId="product-detail-search-descriptors-phrase-status-badge"
              status={phrase.status}
            />
          ) : null}
        </div>
        {phrase.generatedAt ? (
          <span
            data-testid="product-detail-search-descriptors-phrase-generated-at"
            className="text-muted-foreground text-xs"
          >
            Generated {formatDateTime(phrase.generatedAt)}
          </span>
        ) : null}
      </div>

      <DescriptionLevel
        label="Level 3"
        subtitle={LEVEL3_INFO.subtitle}
        derivation={LEVEL3_INFO.derivation}
        description={phrase.level3Description}
        error={phrase.level3Error}
      />

      {/* No Level 3 and no error to explain it — the strategy recorded a row
          but produced nothing deliverable. */}
      {!phrase.level3Description && !phrase.level3Error ? (
        <p
          data-testid="product-detail-search-descriptors-phrase-no-descriptions"
          className="text-muted-foreground text-sm leading-5"
        >
          No descriptions were produced for this product.
        </p>
      ) : null}
    </div>
  );
}

function DescriptorsEmpty({
  title,
  body,
  testId,
  children,
}: {
  title: string;
  body?: string;
  /** Automation hook for this empty state's root. */
  testId?: string;
  /** Extra detail below the message — e.g. the agent's reasoning for an empty run. */
  children?: ReactNode;
}) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center justify-center gap-1 py-10 text-center"
    >
      <p className="text-foreground text-sm leading-5 font-semibold">{title}</p>
      {body ? <p className="text-muted-foreground max-w-md text-sm leading-5">{body}</p> : null}
      {children}
    </div>
  );
}

/**
 * Search Descriptors tab — the output of the two optional POST enrichment steps.
 * `SEARCH_DESCRIPTORS` produces ranked pivot phrases with the search phrases
 * associated with each; `PHRASE_DESCRIPTIONS` turns those phrases into three
 * progressively longer product descriptions.
 *
 * The two halves are branched independently, because the contract lets either run
 * alone — an empty `searchPhrases` array and a null `phraseDescription` mean
 * different things and neither implies the other is absent.
 */
export function SearchDescriptors({ data }: Props) {
  // 404 — neither strategy has ever run for this product. Distinct from a failed
  // request, which the page handles before rendering this component.
  if (!data) {
    return (
      <div
        data-testid="product-detail-search-descriptors"
        className="overflow-hidden rounded-2xl border bg-white p-5 shadow-sm"
      >
        <DescriptorsEmpty
          testId="product-detail-search-descriptors-empty"
          title="No search descriptors generated."
          body="Search descriptors and phrase descriptions run as optional post-enrichment steps. Neither was enabled for this product's latest run."
        />
      </div>
    );
  }

  const volumeNote = keywordSourceNote(data.keywordSource);
  const hasPhrases = data.searchPhrases.length > 0;
  const ratio = data.accuracy ? accuracyRatio(data.accuracy.score) : null;
  const accuracyPct = ratio === null ? null : Math.round(ratio * 100);
  // A row with every field null is a run that produced nothing — the empty state,
  // not a section of absent levels with a lineage note explaining them.
  const phrase =
    data.phraseDescription && hasDescriptionContent(data.phraseDescription)
      ? data.phraseDescription
      : null;

  return (
    <div
      data-testid="product-detail-search-descriptors"
      className="flex flex-col gap-5 overflow-hidden rounded-2xl border bg-white p-5 shadow-sm"
    >
      {/* ---- Search descriptors ---- */}
      <div
        data-testid="product-detail-search-descriptors-search-section"
        className="flex flex-col gap-3"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Search className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
            <h3 className="text-foreground text-sm leading-5 font-semibold">Search Descriptors</h3>
            {/* Null status = only phrase descriptions ran, so there is no
                search-descriptor run to report on. */}
            {data.status ? (
              <StatusBadge
                testId="product-detail-search-descriptors-status-badge"
                status={data.status}
              />
            ) : null}
          </div>
          <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-xs">
            {/* Agent self-rating. Absent on older rows, and hidden rather than
                shown as 0 — no rating is not a bad rating. The score arrives on
                more than one scale, so it is normalised before display. */}
            {accuracyPct !== null ? (
              <span
                data-testid="product-detail-search-descriptors-confidence"
                title={data.accuracy?.reasoning}
                className="cursor-default"
              >
                Confidence{' '}
                <span className="text-foreground font-semibold tabular-nums">{accuracyPct}%</span>
              </span>
            ) : null}
            {data.pivotCount !== null && data.phraseCount !== null ? (
              <span data-testid="product-detail-search-descriptors-counts" className="tabular-nums">
                {data.pivotCount.toLocaleString()} {data.pivotCount === 1 ? 'pivot' : 'pivots'} ·{' '}
                {data.phraseCount.toLocaleString()} {data.phraseCount === 1 ? 'phrase' : 'phrases'}
              </span>
            ) : null}
            {data.generatedAt ? (
              <span data-testid="product-detail-search-descriptors-generated-at">
                Generated {formatDateTime(data.generatedAt)}
              </span>
            ) : null}
          </div>
        </div>

        {volumeNote ? (
          <div
            data-testid="product-detail-search-descriptors-volume-note"
            className="text-muted-foreground flex items-start gap-2 text-sm leading-5"
          >
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>{volumeNote}</span>
          </div>
        ) : null}

        {hasPhrases ? (
          <div
            data-testid="product-detail-search-descriptors-pivots"
            className="flex flex-col gap-3"
          >
            {data.searchPhrases.map((pivot, i) => (
              // `pivotPhrase` isn't guaranteed unique across pivots, so the index
              // tie-breaks the key.
              <PivotCard
                key={`${pivot.pivotPhrase}-${String(i)}`}
                testId={`product-detail-search-descriptors-pivot-${String(i)}`}
                pivot={pivot}
              />
            ))}
          </div>
        ) : (
          <DescriptorsEmpty
            testId="product-detail-search-descriptors-search-empty"
            title="No search descriptors for this product."
            body={searchEmptyBody(data.status)}
          >
            {/* The agent's own account of why it produced nothing — usually the
                most useful thing on an empty run (e.g. every candidate phrase
                carried the brand name, which the rules filter out). */}
            {data.accuracy?.reasoning ? (
              <span className="text-muted-foreground bg-surface-blue mt-3 flex max-w-xl items-start gap-2 rounded-lg px-3 py-2 text-left text-xs leading-5">
                <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                <span>{data.accuracy.reasoning}</span>
              </span>
            ) : null}
          </DescriptorsEmpty>
        )}
      </div>

      {/* ---- Phrase descriptions ---- */}
      <div className="border-t pt-5">
        {phrase ? (
          <PhraseDescriptionSection phrase={phrase} />
        ) : (
          <DescriptorsEmpty
            testId="product-detail-search-descriptors-phrase-empty"
            title="No phrase descriptions generated."
            body={
              // A present-but-empty row and an absent one mean different things:
              // the step ran and produced nothing vs. it was never enabled.
              data.phraseDescription
                ? 'The phrase-description step ran for this product but produced no descriptions. It builds on the search phrases, so it has nothing to work from when none were generated.'
                : "Phrase descriptions are a separate post-enrichment step and were not enabled for this product's latest run."
            }
          />
        )}
      </div>
    </div>
  );
}
